import { useEffect, useState } from "react";
import {
  Archive,
  ArrowLeft,
  Bot,
  ChevronRight,
  Clock3,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Search,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  agenticFlowErrorMessage,
  agenticFlowStatusTone,
  executionDisplayText,
  formatAgenticFlowTime,
  type AgenticFlowExecutionRecord,
  type AgenticFlowListResponse,
  type AgenticFlowProductDetail,
} from "@/lib/agentic-flows";

type Props = {
  canManage: boolean;
  isAdmin: boolean;
  busy: boolean;
  loadList: (includeArchived?: boolean) => Promise<AgenticFlowListResponse>;
  loadDetail: (flowId: string) => Promise<AgenticFlowProductDetail>;
  loadDebug: (flowId: string) => Promise<unknown>;
  run: (flowId: string) => Promise<void>;
  recurrenceAction: (flowId: string, action: "test" | "enable_recurrence" | "recover_recurrence", actionVersion: string) => Promise<void>;
  mutate: (flowId: string, action: "pause" | "resume" | "archive") => Promise<void>;
  checkSetup: (flowId: string, actionVersion: string) => Promise<void>;
  onDiscover: () => void;
  onModify: (flow: AgenticFlowProductDetail) => void;
  onCheckSetup?: () => void;
};

type FailedFlowRecoveryActionsProps = {
  canManage: boolean;
  busy: boolean;
  detail: AgenticFlowProductDetail;
  onCheckSetup?: () => void;
  onDiscover: () => void;
  onModify: (flow: AgenticFlowProductDetail) => void;
};

export const canRecoverFailedFlow = (status: AgenticFlowProductDetail["status"]) => status === "Failed";

export function requestFailedFlowModification(
  detail: AgenticFlowProductDetail,
  onModify: (flow: AgenticFlowProductDetail) => void,
) {
  if (canRecoverFailedFlow(detail.status)) onModify(detail);
}

export function FailedFlowRecoveryActions({ canManage, busy, detail, onDiscover, onModify, onCheckSetup }: FailedFlowRecoveryActionsProps) {
  if (!canManage || !canRecoverFailedFlow(detail.status)) return null;

  return <div className="mt-3">
    <p className="text-xs leading-5 text-slate-600">This failed attempt remains in your history. You can check the existing setup when available, or describe a change to create a new request.</p>
    {detail.setupCheckState === "checking" && <p className="mt-2 text-xs text-sky-800">Checking the existing setup. No new App change was requested.</p>}
    {detail.setupCheckState === "failed" && <p className="mt-2 text-xs text-amber-800">The setup check could not verify this plan. The failed attempt remains in history.</p>}
    <div className="mt-2 flex flex-wrap gap-2">
      {detail.canCheckSetup && onCheckSetup && <Button size="sm" variant="outline" disabled={busy} onClick={onCheckSetup}><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Check setup</Button>}
      <Button size="sm" variant="outline" disabled={busy} onClick={onDiscover}><Search className="mr-1.5 h-3.5 w-3.5" />Discover again</Button>
      <Button size="sm" disabled={busy} onClick={() => requestFailedFlowModification(detail, onModify)}>Describe a change</Button>
    </div>
  </div>;
}

export function ExecutionPlanSummary({ detail }: { detail: AgenticFlowProductDetail }) {
  return <section className="mt-5 border-t border-slate-100 pt-4" aria-label="Execution plan summary">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Execution plan</h3>
    <dl className="mt-3 space-y-3 text-sm">
      <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Objective</dt><dd className="mt-1 leading-5 text-slate-800">{executionDisplayText(detail.purpose)}</dd></div>
      <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">When it runs</dt><dd className="mt-1 flex items-center gap-2 text-slate-800"><Clock3 className="h-4 w-4 text-slate-400" />{executionDisplayText(detail.trigger)}</dd></div>
      {detail.lastRunAt && detail.latestOutcome && <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Last recorded result</dt><dd className="mt-1 leading-5 text-slate-800">{executionDisplayText(detail.latestOutcome)}<span className="mt-1 block text-xs text-slate-500">{formatAgenticFlowTime(detail.lastRunAt)}</span></dd></div>}
    </dl>
    {detail.authority && <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3" aria-label="Verified plan authority">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Verified plan scope</h4>
      <dl className="mt-2 space-y-2 text-xs leading-5 text-slate-800">
        <div><dt className="font-semibold">Reads</dt><dd>{detail.authority.reads.map(executionDisplayText).join(", ")}</dd></div>
        <div><dt className="font-semibold">Changes</dt><dd>{detail.authority.changes.length ? detail.authority.changes.map(executionDisplayText).join(", ") : "None"}</dd></div>
        {detail.authority.externalActions && <div><dt className="font-semibold">External actions</dt><dd>{detail.authority.externalActions.map(executionDisplayText).join(", ")}</dd></div>}
      </dl>
    </div>}
  </section>;
}

const executionStateCopy: Record<AgenticFlowExecutionRecord["status"], string> = {
  started: "Started · no completion recorded",
  succeeded: "Completed",
  failed: "Could not complete",
  outcome_unconfirmed: "Result not confirmed",
};

export function ExecutionRecordList({ records }: { records: AgenticFlowExecutionRecord[] }) {
  if (!records.length) return <div className="rounded-lg border border-dashed border-slate-300 px-3 py-5 text-center text-xs text-slate-500">No run-level execution recorded yet.</div>;
  return <div className="space-y-2">{records.map((item, index) => <article key={`${item.startedAt}-${index}`} className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm">
    <div className="flex items-start justify-between gap-3"><p className="text-xs font-semibold text-slate-900">{executionStateCopy[item.status]}</p><time className="shrink-0 text-[10px] text-slate-500">{formatAgenticFlowTime(item.startedAt)}</time></div>
    {item.origin === "manual" && <p className="mt-1 text-[11px] text-slate-500">Started manually</p>}
    {item.completedAt && <p className="mt-1 text-[11px] text-slate-500">Finished {formatAgenticFlowTime(item.completedAt)}</p>}
    {item.resultSummary && <p className="mt-2 text-xs leading-5 text-slate-700">{executionDisplayText(item.resultSummary)}</p>}
    {item.failureSummary && <p className="mt-2 text-xs leading-5 text-slate-700">{executionDisplayText(item.failureSummary)}</p>}
    {item.effectOutcome === "none_allowed" && <p className="mt-1 text-[11px] text-slate-500">No changes were allowed for this run.</p>}
  </article>)}</div>;
}

export function RecurrenceControls({ detail, canManage, busy, onAction }: {
  detail: AgenticFlowProductDetail;
  canManage: boolean;
  busy: boolean;
  onAction: (action: "test" | "enable_recurrence" | "recover_recurrence") => void;
}) {
  if (!canManage || !detail.recurrence) return null;
  return <div className="mt-3" aria-label="Schedule status">
    <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs font-medium text-sky-900" role="status">
      {detail.recurrence.state}
      {detail.recurrence.state === "Recurring enabled" && detail.scheduleDescription &&
        <span className="mt-1 block font-normal">{executionDisplayText(detail.scheduleDescription)}</span>}
    </p>
    {(detail.recurrence.canTest || detail.recurrence.canEnable || detail.recurrence.canRecover) && <div className="mt-2 flex gap-2">
      {detail.recurrence.canTest && <Button size="sm" disabled={busy} onClick={() => onAction("test")}>Test run</Button>}
      {detail.recurrence.canEnable && <Button size="sm" disabled={busy} onClick={() => onAction("enable_recurrence")}>Make recurring</Button>}
      {detail.recurrence.canRecover && <Button size="sm" disabled={busy} onClick={() => onAction("recover_recurrence")}>Check schedule</Button>}
    </div>}
  </div>;
}

export function AgenticFlowsPanel({
  canManage,
  busy,
  loadList,
  loadDetail,
  run,
  recurrenceAction,
  checkSetup,
  mutate,
  onDiscover,
  onModify,
}: Props) {
  const [data, setData] = useState<AgenticFlowListResponse>({ flows: [], setupAttempts: [], archivedCount: 0 });
  const [detail, setDetail] = useState<AgenticFlowProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const refresh = async (preserveDetail = false) => {
    setLoading(true);
    setError(null);
    try {
      const next = await loadList(showArchived);
      setData(next);
      if (preserveDetail && detail) setDetail(await loadDetail(detail.id));
    } catch (caught) {
      setError(await agenticFlowErrorMessage(caught, "Execution plans could not be loaded. Refresh and try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!busy && detail?.setupCheckState !== "checking") return;
    const interval = window.setInterval(() => void refresh(!!detail), 5_000);
    return () => window.clearInterval(interval);
  }, [busy, detail?.id, detail?.setupCheckState, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (flowId: string) => {
    setLoading(true);
    setError(null);
    try { setDetail(await loadDetail(flowId)); }
    catch (caught) { setError(await agenticFlowErrorMessage(caught, "This execution plan could not be opened. Refresh and try again.")); }
    finally { setLoading(false); }
  };

  const runAction = async (nextAction: "pause" | "resume" | "archive") => {
    if (!detail) return;
    setAction(nextAction);
    setError(null);
    try {
      await mutate(detail.id, nextAction);
      const next = await loadDetail(detail.id);
      setDetail(next);
      setData(await loadList(showArchived));
    } catch (caught) {
      setError(await agenticFlowErrorMessage(caught, "That change could not be completed. Please try again."));
    } finally { setAction(null); }
  };

  const runNow = async () => {
    if (!detail) return;
    setAction("run");
    setError(null);
    try {
      await run(detail.id);
      const next = await loadDetail(detail.id);
      setDetail(next);
      setData(await loadList(showArchived));
    } catch (caught) {
      setError(await agenticFlowErrorMessage(caught, "The review could not be completed. Check recent activity before trying again."));
    } finally { setAction(null); }
  };

  const runRecurrenceAction = async (nextAction: "test" | "enable_recurrence" | "recover_recurrence") => {
    if (!detail) return;
    setAction(nextAction);
    setError(null);
    try {
      await recurrenceAction(detail.id, nextAction, detail.actionVersion ?? "");
      setDetail(await loadDetail(detail.id));
      setData(await loadList(showArchived));
    } catch (caught) {
      setError(await agenticFlowErrorMessage(caught, "The plan could not be changed safely. Refresh its status before trying again."));
      setDetail(await loadDetail(detail.id).catch(() => detail));
    } finally { setAction(null); }
  };

  const runSetupCheck = async () => {
    if (!detail?.canCheckSetup) return;
    setAction("check_setup");
    setError(null);
    try {
      await checkSetup(detail.id, detail.actionVersion ?? "");
      setDetail(await loadDetail(detail.id));
      setData(await loadList(showArchived));
    } catch (caught) {
      setError(await agenticFlowErrorMessage(caught, "The setup could not be checked safely. Refresh its status before trying again."));
      setDetail(await loadDetail(detail.id).catch(() => detail));
    } finally { setAction(null); }
  };

  if (detail) {
    return <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5" aria-label="Execution plan details">
      <button type="button" onClick={() => setDetail(null)} className="mb-4 flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-950">
        <ArrowLeft className="h-3.5 w-3.5" /> All execution plans
      </button>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0"><h2 className="text-base font-semibold text-slate-950">{executionDisplayText(detail.name)}</h2><p className="mt-1 text-xs leading-5 text-slate-600">{executionDisplayText(detail.purpose)}</p></div>
          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-semibold ${agenticFlowStatusTone(detail.status)}`}>{detail.status}</span>
        </div>
        {detail.progressLabel && <div className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />{executionDisplayText(detail.progressLabel)}</div>}
        {detail.warning && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">{executionDisplayText(detail.warning)}</div>}
        <RecurrenceControls detail={detail} canManage={canManage} busy={busy || !!action} onAction={(nextAction) => void runRecurrenceAction(nextAction)} />
        {canRecoverFailedFlow(detail.status) && <FailedFlowRecoveryActions canManage={canManage} busy={busy || !!action} detail={detail} onDiscover={onDiscover} onModify={onModify} onCheckSetup={() => void runSetupCheck()} />}
        <ExecutionPlanSummary detail={detail} />
        {canManage && detail.status !== "Failed" && detail.status !== "Building" && <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          {detail.canRunNow && (!detail.recurrence || detail.recurrence.state === "Recurring enabled") && <Button size="sm" disabled={!!action || busy} onClick={() => void runNow()}>{action === "run" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}Run now</Button>}
          <Button size="sm" variant="outline" disabled={!!action || busy} onClick={() => onModify(detail)}>Describe a change</Button>
          {detail.status === "Active"
            ? <Button size="sm" variant="outline" disabled={!!action || busy} onClick={() => void runAction("pause")}><Pause className="mr-1.5 h-3.5 w-3.5" />Pause</Button>
            : detail.status === "Archived"
            ? <Button size="sm" variant="outline" disabled={!!action || busy} onClick={() => void runAction("resume")}><Play className="mr-1.5 h-3.5 w-3.5" />Restore</Button>
            : detail.status === "Completed"
            ? null
            : <Button size="sm" variant="outline" disabled={!!action || busy} onClick={() => void runAction("resume")}><Play className="mr-1.5 h-3.5 w-3.5" />Resume plan</Button>
            }
          {detail.status !== "Archived" && <Button size="sm" variant="ghost" className="text-slate-600" disabled={!!action || busy} onClick={() => window.confirm("Archive this execution plan? Its history will be kept.") && void runAction("archive")}><Archive className="mr-1.5 h-3.5 w-3.5" />Archive</Button>}
        </div>}
      </div>
      <section className="mt-5" aria-label="Execution record">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Execution record</h3>
        <div className="mt-2"><ExecutionRecordList records={detail.executionRecords ?? []} /></div>
      </section>
      {detail.activities.length > 0 && <section className="mt-5" aria-label="Other recorded activity"><h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Other recorded activity</h3><div className="mt-2 space-y-2">{detail.activities.map((item) => <article key={item.id} className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm"><div className="flex items-start justify-between gap-3"><p className="text-xs font-semibold text-slate-900">{executionDisplayText(item.title)}</p><time className="shrink-0 text-[10px] text-slate-400">{formatAgenticFlowTime(item.occurredAt)}</time></div><p className="mt-1 text-xs leading-5 text-slate-600">{executionDisplayText(item.summary)}</p>{item.effectSummary && <p className="mt-1 text-xs font-medium text-slate-700">{executionDisplayText(item.effectSummary)}</p>}</article>)}</div></section>}
      {error && <p className="mt-3 text-xs text-rose-700" role="alert">{error}</p>}
    </div>;
  }

  return <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5" aria-label="Execution">
    <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-800 p-4 text-white shadow-sm">
      <Sparkles className="h-5 w-5 text-cyan-300" />
      <h2 className="mt-3 text-base font-semibold">Execution</h2>
      <p className="mt-1 text-xs leading-5 text-slate-300">See the plans this App follows and the work it has recorded.</p>
      {canManage && <Button size="sm" className="mt-4 bg-white text-slate-950 hover:bg-slate-100" disabled={busy} onClick={onDiscover}><Search className="mr-1.5 h-3.5 w-3.5" />Explore new work</Button>}
    </div>
    <div className="mt-5 flex items-center justify-between"><h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Execution plans</h2><button type="button" onClick={() => void refresh()} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-200" aria-label="Refresh execution plans"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></button></div>
    {loading && !data.flows.length ? <div className="py-10 text-center text-xs text-slate-500"><Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" />Loading execution plans</div> : data.flows.length ? <div className="mt-2 space-y-2">{data.flows.map((flow) => <button key={flow.id} type="button" onClick={() => void open(flow.id)} className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-slate-300 hover:shadow"><div className="flex items-start gap-3"><span className="mt-0.5 rounded-lg bg-slate-100 p-2"><Bot className="h-4 w-4 text-slate-700" /></span><span className="min-w-0 flex-1"><span className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold text-slate-950">{executionDisplayText(flow.name)}</span><span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${agenticFlowStatusTone(flow.status)}`}>{flow.status}</span></span><span className="mt-1 block text-xs leading-5 text-slate-600">{executionDisplayText(flow.purpose)}</span><span className="mt-2 block text-[11px] text-slate-500">{executionDisplayText(flow.trigger)}</span>{flow.recurrence && <span className="mt-1 block text-[11px] font-medium text-slate-700">{flow.recurrence.state}</span>}{flow.latestOutcome && <span className="mt-1 block text-[11px] font-medium text-slate-700">Last recorded result: {executionDisplayText(flow.latestOutcome)}</span>}</span><ChevronRight className="mt-2 h-4 w-4 shrink-0 text-slate-400" /></div></button>)}</div> : !error && <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center"><Bot className="mx-auto h-6 w-6 text-slate-400" /><p className="mt-2 text-sm font-medium text-slate-800">No execution plans yet</p><p className="mt-1 text-xs leading-5 text-slate-500">Plans will appear here after they are created.</p></div>}
    {data.archivedCount > 0 && <button type="button" className="mt-4 text-xs font-medium text-slate-500 hover:text-slate-900" onClick={() => setShowArchived((value) => !value)}>{showArchived ? "Hide archived" : `Show archived (${data.archivedCount})`}</button>}
    {(data.setupAttempts ?? []).length > 0 && <section className="mt-5" aria-label="Plan setup attempts"><h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Setup attempts</h2><p className="mt-1 text-xs leading-5 text-slate-500">These plans are not ready to run. Failed attempts remain in the record.</p><div className="mt-2 space-y-2">{(data.setupAttempts ?? []).map((flow) => <button key={flow.id} type="button" onClick={() => void open(flow.id)} className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"><span className="flex items-center justify-between gap-2"><span className="truncate text-sm font-medium text-slate-800">{executionDisplayText(flow.name)}</span><span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${agenticFlowStatusTone(flow.status)}`}>{flow.status}</span></span><span className="mt-1 block text-xs leading-5 text-slate-500">{flow.status === "Failed" ? "Not activated · history retained" : "Validation in progress"}</span></button>)}</div></section>}
    {error && <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-800" role="alert">{error}</p>}
  </div>;
}
